
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Plus, Trash2, Loader2, Linkedin, Github, Twitter, Dribbble, Globe } from 'lucide-react';

interface SocialLinkItem {
    id: string;
    platform: string;
    url: string;
    username: string;
    icon: string;
}

export default function SocialLinksManager() {
    const [links, setLinks] = useState<SocialLinkItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [newItem, setNewItem] = useState<Partial<SocialLinkItem>>({
        platform: 'LinkedIn',
        url: '',
        username: '',
        icon: 'Linkedin'
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const { data, error } = await supabase
                .from('site_content')
                .select('content')
                .eq('section_key', 'social_links')
                .maybeSingle();

            if (error) throw error;
            if (data && data.content) {
                setLinks(data.content);
            }
        } catch (err) {
            console.error('Error fetching social links:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (updatedList: SocialLinkItem[]) => {
        setSaving(true);
        try {
            const { error } = await supabase
                .from('site_content')
                .upsert({
                    section_key: 'social_links',
                    content: updatedList,
                    updated_at: new Date().toISOString()
                });

            if (error) throw error;
            setLinks(updatedList);
            setNewItem({ platform: 'LinkedIn', url: '', username: '', icon: 'Linkedin' });
        } catch (err) {
            console.error('Error saving social links:', err);
            alert('Failed to save social links');
        } finally {
            setSaving(false);
        }
    };

    const addItem = () => {
        if (!newItem.url) return;

        const itemToAdd: SocialLinkItem = {
            id: Math.random().toString(36).substr(2, 9),
            platform: newItem.platform!,
            url: newItem.url!,
            username: newItem.username || '',
            icon: newItem.platform || 'Globe'
        };

        const updated = [...links, itemToAdd];
        handleSave(updated);
    };

    const deleteItem = (id: string) => {
        if (!confirm('Delete this link?')) return;
        const updated = links.filter(i => i.id !== id);
        handleSave(updated);
    };

    const getIcon = (name: string) => {
        switch (name.toLowerCase()) {
            case 'linkedin': return <Linkedin size={20} />;
            case 'github': return <Github size={20} />;
            case 'twitter': return <Twitter size={20} />;
            case 'dribbble': return <Dribbble size={20} />;
            default: return <Globe size={20} />;
        }
    };

    if (loading) return <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-blue-500" /></div>;

    return (
        <div className="space-y-8">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
                <h2 className="text-xl font-semibold text-white mb-4">Add Social Link</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">Platform</label>
                        <select
                            value={newItem.platform}
                            onChange={(e) => setNewItem({ ...newItem, platform: e.target.value, icon: e.target.value })}
                            className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        >
                            <option value="LinkedIn">LinkedIn</option>
                            <option value="GitHub">GitHub</option>
                            <option value="Twitter">Twitter</option>
                            <option value="Dribbble">Dribbble</option>
                            <option value="Website">Website</option>
                        </select>
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-400 mb-1">URL</label>
                        <input
                            type="url"
                            value={newItem.url}
                            onChange={(e) => setNewItem({ ...newItem, url: e.target.value })}
                            className="w-full bg-gray-800 border-gray-700 rounded-md text-white px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            placeholder="https://..."
                        />
                    </div>
                    <button
                        onClick={addItem}
                        disabled={saving || !newItem.url}
                        className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-md font-medium transition-colors disabled:opacity-50"
                    >
                        {saving ? <Loader2 className="animate-spin h-5 w-5" /> : <Plus className="h-5 w-5" />}
                        Add
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {links.map((link) => (
                    <div key={link.id} className="bg-gray-900 border border-gray-800 rounded-lg p-4 flex items-center justify-between group hover:border-gray-700 transition-colors">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-gray-800 flex items-center justify-center text-blue-400">
                                {getIcon(link.platform)}
                            </div>
                            <div className="overflow-hidden">
                                <h3 className="font-medium text-white">{link.platform}</h3>
                                <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-400 hover:underline truncate block">
                                    {link.url}
                                </a>
                            </div>
                        </div>
                        <button
                            onClick={() => deleteItem(link.id)}
                            className="text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity p-2"
                        >
                            <Trash2 size={18} />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}
